import React, { useMemo, useState, useRef, useEffect } from 'react'
import { Box, CircularProgress, Alert, Typography } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import type { PlatinumGraphsProps } from './types'
import { convertPlatinumToTable, transposeParsedTable, extractRowLabels, extractColumnLabels, extractRowTypes, extractColumnTypes } from './utils/convertPlatinumData'
import { generators } from './generators'
import { GeneratorError, CHART_GRID_TOP_NO_LEGEND, CHART_GRID_TOP_WITH_LEGEND } from './generators/helpers'
import { getSelectedRowLabels, getSelectedColumnLabels, getChartRowPositions } from './utils/selectionHelpers'
import { applyChartDesign } from '../../utils/applyChartDesign'

/**
 * PlatinumGraphs Component
 *
 * A standalone, reusable component for rendering charts from PlatinumData.
 * Decoupled from the table component for maximum flexibility.
 */
export const PlatinumGraphs: React.FC<PlatinumGraphsProps> = ({
  data,
  config,
  onConfigChange,
  showControls = false,
  className = '',
  isLoading = false,
  error: propsError = null,
  allRowTypes,
  allColumnTypes,
  respectParentHeight = false,
  colorMap,
  gradientMap,
  hideLegend = false,
  onLegendToggle,
  design,
}) => {
  const [internalError, setInternalError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const echartsRef = useRef<ReactECharts | null>(null)

  // Add resize observer to handle container size changes. The resize runs on
  // the next animation frame — not a 100ms timeout — so the canvas tracks the
  // container during continuous resizes (dashboard boundary drags, #1177)
  // instead of lagging behind with a stale pixel size. rAF (rather than a
  // synchronous call) keeps the ResizeObserver callback from mutating layout
  // in the same observation cycle.
  useEffect(() => {
    if (!containerRef.current) return

    let frame = 0
    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const echartsInstance = echartsRef.current?.getEchartsInstance()
        if (echartsInstance) {
          echartsInstance.resize()
        }
      })
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      cancelAnimationFrame(frame)
      resizeObserver.disconnect()
    }
  }, [])

  // Parse Platinum Data (Memoized)
  const parsedResult = useMemo(() => {
    setInternalError(null)

    if (!data) {
      return { data: null, error: null, originalData: null }
    }

    console.log('PlatinumGraphs: Converting Platinum data...')
    const originalParsedData = convertPlatinumToTable(data, config.dataType)
    if (!originalParsedData) {
      return { data: null, error: 'Failed to convert Platinum table data.', originalData: null }
    }

    // Keep reference to original data structure before transposition
    let parsedData = originalParsedData

    // Transpose data if switchAxis is enabled
    if (config.switchAxis) {
      console.log('PlatinumGraphs: Transposing table data (switchAxis enabled)')
      parsedData = transposeParsedTable(originalParsedData)
    }

    return { data: parsedData, error: null, originalData: originalParsedData }
  }, [data, config.dataType, config.switchAxis])

  const parsedData = parsedResult.data
  const parsingError = parsedResult.error
  const originalParsedData = parsedResult.originalData

  // Extract row and column types from data if not provided
  const rowTypes = useMemo(() => {
    if (allRowTypes) return allRowTypes
    if (!data) return []
    return extractRowTypes(data)
  }, [allRowTypes, data])

  const columnTypes = useMemo(() => {
    if (allColumnTypes) return allColumnTypes
    if (!data) return []
    return extractColumnTypes(data)
  }, [allColumnTypes, data])

  // Extract all row and column labels for selection helpers
  const allRows = useMemo(() => {
    if (!data) return []
    return extractRowLabels(data)
  }, [data])

  const allColumns = useMemo(() => {
    if (!data) return []
    return extractColumnLabels(data)
  }, [data])

  // Compute selected labels from ORIGINAL data (before transposition)
  const originalSelectedRowLabels = useMemo(() => {
    if (!originalParsedData) return []
    return getSelectedRowLabels(config.selectedRows, allRows, rowTypes)
  }, [config.selectedRows, allRows, rowTypes, originalParsedData])

  const originalSelectedColumnLabels = useMemo(() => {
    if (!originalParsedData) return []
    // allColumns comes from extractColumnLabels which doesn't have empty string at index 0
    // So we don't need the parsedData format offset
    return getSelectedColumnLabels(config.selectedColumns, allColumns, columnTypes)
  }, [config.selectedColumns, allColumns, columnTypes, originalParsedData])

  // When switchAxis is enabled, swap the row/column selections
  // Because rows become columns and columns become rows after transpose
  const useSelectedRows = useMemo(() => {
    if (config.switchAxis) {
      return originalSelectedColumnLabels
    } else {
      return originalSelectedRowLabels
    }
  }, [config.switchAxis, originalSelectedRowLabels, originalSelectedColumnLabels])

  const useSelectedColumns = useMemo(() => {
    if (config.switchAxis) {
      return originalSelectedRowLabels
    } else {
      return originalSelectedColumnLabels
    }
  }, [config.switchAxis, originalSelectedRowLabels, originalSelectedColumnLabels])

  // #1808: the same selection as POSITIONS of the rows the generator sees
  // (after a switchAxis transpose those are the original columns). The
  // generators filter by these and keep the names only as a fallback, so two
  // rows labelled "Sky" stay two bars.
  const useSelectedRowPositions = useMemo(() => {
    return config.switchAxis
      ? getChartRowPositions(config.selectedColumns, allColumns, columnTypes)
      : getChartRowPositions(config.selectedRows, allRows, rowTypes)
  }, [config.switchAxis, config.selectedRows, config.selectedColumns, allRows, allColumns, rowTypes, columnTypes])

  const useSelectedColumn = useMemo(() => {
    const colData = parsedData?.headers || []
    if (config.selectedColumn) return config.selectedColumn
    // Auto-select first non-empty header
    return colData.find(h => h && h.trim() !== '') || colData[0]
  }, [config.selectedColumn, parsedData])

  // Generate Chart Options (Memoized)
  const chartOptions = useMemo(() => {
    if (config.chartType === 'table' || internalError || parsingError || !parsedData) {
      console.log(
        `PlatinumGraphs: Skipping chart generation. Reason: chartType=${config.chartType}, internalError=${!!internalError}, parsingError=${!!parsingError}, parsedData=${!!parsedData}`
      )
      return null
    }

    // For bar/column charts, require selectedColumn
    if ((config.chartType === 'bar' || config.chartType === 'column') && !useSelectedColumn) {
      console.log(
        `PlatinumGraphs: Waiting for selectedColumn setting for single-select ${config.chartType} chart.`
      )
      return null
    }

    // For other charts, require row/column filters
    if (
      config.chartType !== 'bar' &&
      config.chartType !== 'column' &&
      config.chartType !== 'pie' &&
      (!useSelectedRows || useSelectedRows.length === 0 || !useSelectedColumns || useSelectedColumns.length === 0)
    ) {
      console.log(
        `PlatinumGraphs: Waiting for filter settings (rows/columns) for ${config.chartType} chart.`
      )
      return null
    }

    const generator = generators[config.chartType]
    if (!generator) {
      console.error(`PlatinumGraphs: No generator found for chart type: ${config.chartType}`)
      setInternalError(`No generator found for chart type: ${config.chartType}`)
      return null
    }

    try {
      console.log(`PlatinumGraphs: Generating options for ${config.chartType} with config:`, config)

      // useSelectedRows and useSelectedColumns already handle the switchAxis swap
      const settings = {
        dataType: config.dataType,
        selectedHeader: useSelectedColumn,
        selectedRows: useSelectedRows,
        selectedColumns: useSelectedColumns,
        selectedRowPositions: useSelectedRowPositions,
        colorMap,
        gradientMap,
        showDataLabels: config.showDataLabels,
        hideColumnCaption: config.hideColumnCaption,
        sortOrder: config.sortOrder,
      }

      const options = generator(parsedData, '', settings)
      if (!options) {
        console.warn(`PlatinumGraphs: Generator for ${config.chartType} returned null options.`)
        setInternalError(`Generator failed for ${config.chartType}.`)
        return null
      }
      setInternalError(null)
      return applyChartDesign(options, design, config.dataType)
    } catch (e: any) {
      console.error(`Error generating options for ${config.chartType}:`, e)
      const errMsg =
        e instanceof GeneratorError ? e.message : `Error generating chart options: ${e.message}`
      setInternalError(errMsg)
      return null
    }
  }, [parsedData, config, internalError, parsingError, useSelectedRows, useSelectedColumns, useSelectedRowPositions, useSelectedColumn, colorMap, gradientMap, design])

  // Force ECharts to apply new options when they change.
  // echarts-for-react's isEqual check can miss structural changes in nested axis data,
  // so we call setOption directly as a workaround.
  useEffect(() => {
    if (chartOptions && echartsRef.current) {
      const echartsInstance = echartsRef.current.getEchartsInstance()
      if (echartsInstance) {
        echartsInstance.setOption(chartOptions, true) // true = notMerge
        const timeoutId = setTimeout(() => echartsInstance.resize(), 150)
        return () => clearTimeout(timeoutId)
      }
    }
  }, [chartOptions])

  // Rendering Logic
  const displayError = propsError || internalError || parsingError
  const canRenderChart = !isLoading && !displayError && parsedData && config.chartType !== 'table'

  // Loading State
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: '300px',
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Chart Data...</Typography>
      </Box>
    )
  }

  // Error State
  if (displayError) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: '300px',
          p: 2,
        }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {displayError}
        </Alert>
      </Box>
    )
  }

  // No Data State
  if (!data) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: '300px',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Select or load data to view the chart.
        </Typography>
      </Box>
    )
  }

  // No Parsed Data State
  if (!parsedData) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: '300px',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Could not parse data. Check format.
        </Typography>
      </Box>
    )
  }

  // Main Render
  return (
    <Box
      ref={containerRef}
      className={className}
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Chart Area */}
      <Box
        sx={{
          flexGrow: 1,
          height: '100%',
          minHeight: respectParentHeight ? 0 : '400px',
          maxHeight: respectParentHeight ? '100%' : '80vh',
          width: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          // ALWAYS hidden, never auto (#1177). The ECharts canvas carries a
          // fixed pixel size stamped at its last resize(), so mid-resize it
          // briefly overflows this box. With `auto`, that flicker became a
          // permanent grey scrollbar on Windows: the classic scrollbar eats
          // ~16px of the scrollport, which keeps the canvas overflowing,
          // which keeps the scrollbar — self-sustaining after the drag ends.
          // A canvas always re-sizes itself to this box, so scrolling it is
          // never meaningful; clip until the next resize() snaps it to fit.
          overflow: 'hidden',
        }}
      >
        {(() => {
          // Check for missing filter selections
          const multiSelectChartTypes = ['line', 'clusteredColumn', 'clusteredBar', 'stackedBar', 'scatter', 'stackedColumn']
          if (
            multiSelectChartTypes.includes(config.chartType) &&
            (!useSelectedColumns || useSelectedColumns.length === 0)
          ) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="body1" color="text.secondary" align="center">
                  Please select at least one column from the filter
                  <br />
                  to display chart data.
                </Typography>
              </Box>
            )
          }

          // Show ECharts component if options are generated
          if (chartOptions) {
            // Apply legend hiding if requested. The line chart's legend can
            // be an ARRAY (two stacked scroll rows, #1184) — object-spreading
            // an array turns it into `{0: ..., 1: ..., show: false}`, which
            // ECharts ignores, so patch each entry instead.
            const legendOption = (chartOptions as any).legend
            const gridOption = (chartOptions as any).grid
            const finalOptions = hideLegend
              ? {
                  ...chartOptions,
                  legend: Array.isArray(legendOption)
                    ? legendOption.map((entry) => ({ ...entry, show: false }))
                    : { ...(legendOption || {}), show: false },
                  // #1201: reclaim the band the hidden legend was reserving.
                  // `show: false` stops the legend PAINTING but leaves the
                  // generator's grid.top at CHART_GRID_TOP_WITH_LEGEND, so
                  // every hideLegend chart carried an empty 56px stripe above
                  // the plot. Only the untouched generator value is lowered —
                  // a grid.top a Chart Design override put there is left
                  // alone, and pie (no grid at all) is unaffected.
                  ...(gridOption &&
                  !Array.isArray(gridOption) &&
                  gridOption.top === CHART_GRID_TOP_WITH_LEGEND
                    ? { grid: { ...gridOption, top: CHART_GRID_TOP_NO_LEGEND } }
                    : {}),
                }
              : chartOptions

            return (
              <ReactECharts
                ref={echartsRef}
                option={finalOptions}
                style={{
                  height: '100%',
                  width: '100%',
                }}
                notMerge={true}
                lazyUpdate={false}
                onEvents={onLegendToggle ? {
                  legendselectchanged: (e: { name?: string }) => {
                    if (e?.name) onLegendToggle(e.name)
                  },
                } : undefined}
              />
            )
          }

          // Show Error/Info/Loading placeholder
          return (
            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              {internalError ? (
                <Alert severity="warning" sx={{ width: '100%' }}>
                  {internalError}
                </Alert>
              ) : (
                <Typography color="text.secondary">Generating chart...</Typography>
              )}
            </Box>
          )
        })()}
      </Box>
    </Box>
  )
}

export default PlatinumGraphs
